import { IApi } from '@/setup/api-client'
import { useApiQuery } from '@/utils/api/api-query'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import type {
    GetRealtimeChannelTokenData,
    GetRealtimeChannelTokenBody,
    GetRealtimeChannelTokenParams,
} from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/realtime/get-token'

export class GetRealtimeChannelTokenQuery {
    static getKey({
        organizationId,
        brandUuid,
        channel,
        topics,
    }: {
        organizationId: string
        brandUuid: string
        channel: string
        topics: string[]
    }) {
        return ['organization', organizationId, 'brand', brandUuid, 'realtime', 'channel', channel, 'token', ...topics]
    }

    static async call<T>(api: IApi, params: GetRealtimeChannelTokenParams, body: GetRealtimeChannelTokenBody) {
        return api
            .post<
                GetRealtimeChannelTokenData,
                GetRealtimeChannelTokenBody
            >(`/organization/${params.organization_id}/brands/${params.brand_slug}/realtime/get-token`, body)
            .then((res) => res?.data.token as T | undefined)
    }
}

export function useGetRealtimeChannelToken<T>({ channel, topics }: { channel: string | undefined; topics: string[] }) {
    const { api, apiParams } = useApiToolbox()

    const computedChannel = channel || ''

    const payload = {
        organizationId: apiParams.organizationId,
        brandSlug: apiParams.brandSlug,
        channel: computedChannel,
        topics,
    }

    return useApiQuery({
        queryKey: GetRealtimeChannelTokenQuery.getKey({
            organizationId: payload.organizationId,
            brandUuid: payload.brandSlug,
            channel: payload.channel,
            topics: payload.topics,
        }),
        queryFn: () =>
            GetRealtimeChannelTokenQuery.call<T>(
                api,
                {
                    organization_id: payload.organizationId,
                    brand_slug: payload.brandSlug,
                },
                {
                    topics: payload.topics,
                    channel: payload.channel,
                }
            ),
        enabled: Boolean(payload.channel && payload.brandSlug && payload.organizationId && payload.topics.length > 0),
    })
}
